/* eslint-disable @typescript-eslint/no-empty-function */
import { Injectable, NgZone, OnDestroy, Inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { SafeLogger } from '@backbase/shared/util/app-core';

@Injectable({
    providedIn: 'root'
})
export class AccountsInterceptorService implements OnDestroy {
    private readonly STORAGE_KEY = 'sbk_account_balance_visibility';
    private readonly MASKED_ROUTES = [
        '/my-accounts/list',
        '/my-accounts/manage'
    ];
    private _isAmountVisible!: BehaviorSubject<boolean>;
    public isAmountVisible$: any;
    private mutationObserver: MutationObserver | null = null;
    private isProcessing = false;

    get isAmountVisible(): boolean {
        return this._isAmountVisible.value;
    }

    constructor(
        private ngZone: NgZone,
        @Inject(DOCUMENT) private document: Document,
        private readonly logger: SafeLogger,
    ) {
        // Load the saved state from localStorage, default to false (hidden)
        const savedState = this.loadVisibilityState();
        this._isAmountVisible = new BehaviorSubject<boolean>(savedState);
        this.isAmountVisible$ = this._isAmountVisible.asObservable();

        // Apply initial masking
        this.initializeMasking();

        // Set up mutation observer to handle dynamically added elements
        this.setupMutationObserver();
    }

    private shouldApplyMasking(): boolean {
        const currentPath = this.document.location.pathname;
        // Only apply masking on routes in the MASKED_ROUTES list
        // Handle internationalized routes (e.g., /en/my-accounts/list or /my-accounts/list)
        return this.MASKED_ROUTES.some(route => {
            // Check if path matches directly or after a locale prefix (e.g., /en, /es, /fr)
            return currentPath.includes(route) || 
                   currentPath.match(new RegExp(`^/[a-z]{2}${route.replace(/\//g, '\\/')}`));
        });
    }

    ngOnDestroy(): void {
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
            this.mutationObserver = null;
        }
    }

    private loadVisibilityState(): boolean {
        try {
            const saved = localStorage.getItem(this.STORAGE_KEY);
            return saved === 'true'; // Default to false (hidden) if not set
        } catch (error) {
            this.logger.warn('Failed to load visibility state from localStorage', error);
            return false; // Default to hidden
        }
    }

    private saveVisibilityState(isVisible: boolean): void {
        try {
            localStorage.setItem(this.STORAGE_KEY, String(isVisible));
        } catch (error) {
            this.logger.warn('Failed to save visibility state to localStorage', error);
        }
    }

    private initializeMasking(): void {
        this.ngZone.runOutsideAngular(() => {
            // Use requestAnimationFrame for better timing with rendering
            requestAnimationFrame(() => {
                this.applyMaskingToExistingElements();
            });
        });
    }

    private setupMutationObserver(): void {
        this.ngZone.runOutsideAngular(() => {
            this.mutationObserver = new MutationObserver((mutations) => {
                // Check if any new elements were added
                const hasNewElements = mutations.some(mutation =>
                    mutation.addedNodes.length > 0 ||
                    mutation.type === 'childList'
                );

                if (hasNewElements && !this.isProcessing) {
                    requestAnimationFrame(() => {
                        this.applyMaskingToExistingElements();
                    });
                }
            });

            // Observe the document body for changes
            this.mutationObserver.observe(document.body, {
                childList: true,
                subtree: true
            });
        });
    }

    private applyMaskingToExistingElements(): void {
        if (this.isProcessing) return;

        this.isProcessing = true;

        try {
            const amountElements = document.querySelectorAll('.bb-amount__value');
            const shouldApplyMasking = this.shouldApplyMasking();

            // If we're not on a route where masking should be applied, always show balances
            const shouldBeVisible = shouldApplyMasking ? this._isAmountVisible.value : true;

            amountElements.forEach((element: Element) => {
                const htmlElement = element as HTMLElement;

                if (shouldBeVisible) {
                    // Restore original HTML if it exists
                    const originalHtml = htmlElement.getAttribute('data-original-html');
                    if (originalHtml && htmlElement.innerHTML === '**********') {
                        htmlElement.innerHTML = originalHtml;
                        htmlElement.removeAttribute('data-masked');
                    }
                } else {
                    // Only mask if not already masked
                    const isMasked = htmlElement.getAttribute('data-masked') === 'true';
                    if (!isMasked && htmlElement.innerHTML !== '**********') {
                        // Save the original HTML before masking
                        htmlElement.setAttribute('data-original-html', htmlElement.innerHTML);
                        htmlElement.innerHTML = '**********';
                        htmlElement.setAttribute('data-masked', 'true');
                    }
                }
            });
        } finally {
            this.isProcessing = false;
        }
    }

    applyMasking(shouldBeVisible: boolean): void {
        // Only apply masking if we're on a route where it should be applied
        if (!this.shouldApplyMasking()) {
            return;
        }

        // Update the state
        this._isAmountVisible.next(shouldBeVisible);

        // Save to localStorage
        this.saveVisibilityState(shouldBeVisible);

        // Apply masking immediately
        this.ngZone.runOutsideAngular(() => {
            requestAnimationFrame(() => {
                this.applyMaskingToExistingElements();
                this.ngZone.run(() => { });
            });
        });
    }

    toggleVisibility(): void {
        this.applyMasking(!this.isAmountVisible);
    }

    setVisibility(shouldBeVisible: boolean): void {
        // Only apply if we're on a route where masking should be applied and the state is actually changing
        if (this.shouldApplyMasking() && this._isAmountVisible.value !== shouldBeVisible) {
            this.applyMasking(shouldBeVisible);
        }
    }
}